import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import { loadState, saveState } from './utils/localStorage';

import channelReducer from './features/channelSlice';
import deviceReducer from './features/deviceSlice';
import notificationReducer, { addNotification, dismissNotification } from './features/notificationSlice';
import sidePanelReducer from './features/sidePanelSlice';
import videoReducer from './features/videoSlice';
import liveReducer from './features/liveSlice';
import authReducer from './features/authSlice';
import adminReducer from './features/adminSlice';
import audioReducer from './features/audioSlice';

// Load persisted state and apply migrations/fixes
const rawPersistedState = loadState();
const persistedState = rawPersistedState ? {
  ...rawPersistedState,
  channel: rawPersistedState.channel ? {
    ...rawPersistedState.channel,
    // Fix double /prweb in persisted hostServer
    hostServer: rawPersistedState.channel.hostServer?.includes('/prweb/prweb')
      ? rawPersistedState.channel.hostServer.replace('/prweb/prweb', '/prweb')
      : rawPersistedState.channel.hostServer
  } : undefined
} : undefined;

// Log the migration if applied
if (rawPersistedState?.channel?.hostServer?.includes('/prweb/prweb')) {
  console.log('🔧 Migration: Fixed double /prweb in persisted state:', {
    before: rawPersistedState.channel.hostServer,
    after: persistedState.channel.hostServer
  });
}

const notificationMiddleware = createListenerMiddleware();

// Auto-dismiss notifications after 5 seconds
notificationMiddleware.startListening({
  actionCreator: addNotification,
  effect: async (action, listenerApi) => {
    const notificationId = action.payload.id;
    const notificationTitle = action.payload.title;
    
    console.log(`[Auto-dismiss] Starting 5s timer for notification: ${notificationId} - "${notificationTitle}"`);
    
    // Use a simple setTimeout approach that's more predictable
    const timeoutId = setTimeout(() => {
      // Check if notification still exists before auto-dismissing
      const currentState = listenerApi.getState();
      const notificationExists = currentState.notifications.notifications.some(n => n.id === notificationId);
      
      if (notificationExists) {
        console.log(`[Auto-dismiss] Auto-dismissing notification: ${notificationId} - "${notificationTitle}"`);
        listenerApi.dispatch(dismissNotification(notificationId));
      } else {
        console.log(`[Auto-dismiss] Notification already dismissed: ${notificationId} - "${notificationTitle}"`);
      }
    }, 5000);
    
    // Store timeout ID for potential cleanup
    // We'll clean up the timeout if the notification is manually dismissed
    return () => {
      console.log(`[Auto-dismiss] Cleanup timeout for notification: ${notificationId} - "${notificationTitle}"`);
      clearTimeout(timeoutId);
    };
  },
});

export const store = configureStore({
  reducer: {
    channel: channelReducer,
    device: deviceReducer,
    notifications: notificationReducer,
    sidePanel: sidePanelReducer,
    videos: videoReducer,
    live: liveReducer,
    auth: authReducer,
    admin: adminReducer,
    audio: audioReducer,
  },
  preloadedState: persistedState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(notificationMiddleware.middleware),
});

// If localStorage is used for hostServer/applicationAlias, update to xLabbHostServer/xLabbApplicationAlias
// Otherwise, ensure config object is using new keys if needed
store.subscribe(() => {
  saveState({
    channel: store.getState().channel,
  });
});

// Expose store to window for debugging (development only)
if (process.env.NODE_ENV === 'development') {
  window.store = store;
}

export default store;
